/** @jsxImportSource react */
import { useState } from 'react'
import { Alert, Box, Button, Link, TextField } from '@mui/material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { authAPI } from '../api/auth'
import { getErrorMessage } from '../utils/apiErrors'
import AuthCard from '../components/common/AuthCard'

/** The emailed link: /reset-password#token=… (a #fragment never reaches servers or Referer headers). */
const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get('token') || '')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [formError, setFormError] = useState(null)
  const reset = useMutation({
    mutationFn: () => authAPI.resetPassword(token, password),
    onSuccess: () => {
      window.history.replaceState(null, '', window.location.pathname) // drop the used token from the address bar
      navigate('/login?reset=1', { replace: true })
    },
  })

  if (!token) {
    return (
      <AuthCard subtitle="Set a new password">
        <Alert severity="warning" sx={{ mb: 2 }}>This link is incomplete. Open the link from the email again, or ask for a new one.</Alert>
        <Link component={RouterLink} to="/forgot-password" variant="body2">Ask for a new link</Link>
      </AuthCard>
    )
  }

  const submit = (e) => {
    e.preventDefault()
    if (password.length < 8) return setFormError('Use at least 8 characters')
    if (password !== confirm) return setFormError('Passwords don’t match')
    setFormError(null)
    reset.mutate()
  }
  return (
    <AuthCard subtitle="Set a new password">
      <Box component="form" onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {(formError || reset.error) && <Alert severity="error">{formError || getErrorMessage(reset.error)}</Alert>}
        <TextField label="New password" type="password" autoComplete="new-password" autoFocus required value={password} onChange={(e) => setPassword(e.target.value)} helperText="At least 8 characters" />
        <TextField label="Confirm new password" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        <Button type="submit" variant="contained" disabled={reset.isPending}>{reset.isPending ? 'Saving…' : 'Set new password'}</Button>
      </Box>
      {reset.error && <Link component={RouterLink} to="/forgot-password" variant="body2" sx={{ display: 'inline-block', mt: 2 }}>Ask for a new link</Link>}
    </AuthCard>
  )
}

export default ResetPasswordPage
